import { makeAutoObservable, runInAction } from 'mobx';
import { ordersApi, type OrderListParams } from '@/api/orders';
import { isAbortError, RequestSequencer } from '@/api/requests';
import { normalizeApiError, type ApiError } from '@/lib/errors';
import type { Order } from '@/types/orders';
import type { Paginated } from '@/types/common';

/**
 * OrderStore (V1-05) — active queue (/orders/new), paginated history
 * (/orders), and per-order detail cache, following the MobX server-state
 * discipline (01-architecture.md §4): status enum with retained data,
 * RequestSequencer stale guards, explicit invalidation, reset() on scope
 * change (lead wires auth.registerScopeListener → reset()).
 *
 * Realtime/polling integration (06-realtime.md §4):
 *  - `notifyExternalChange()` is the COALESCED entrypoint for socket hints:
 *    a burst of events inside the coalesce window produces ONE HTTP
 *    reconcile; hints during an in-flight reconcile re-arm one more.
 *  - `reconcileActive()` is the direct fetch used by the polling fallback;
 *    it returns success/failure so the poller can back off.
 *  - `onActiveReconciled` fires after every successful active fetch — the
 *    RealtimeStore chime scan hangs off it so event, polling, and reconnect
 *    paths converge on one notification decision.
 *
 * Status mutations go through `transition()`: the current PUT returns NO
 * order body (docs/05a-api-baseline.md §10.2), so success ALWAYS re-fetches
 * the queue (and open detail/loaded history) before resolving.
 */

export type LoadStatus = 'idle' | 'loading' | 'success' | 'refreshing' | 'error';

export interface ExternalChangeHint {
  kind: 'new' | 'status';
  /** Order id when the event payload carried one; null → whole-board refresh. */
  orderId: number | null;
}

export interface OrderDetailEntry {
  order: Order | null;
  status: LoadStatus;
  error: ApiError | null;
}

export interface TransitionResult {
  ok: boolean;
  error?: ApiError;
}

export interface OrderStoreOptions {
  api?: Pick<typeof ordersApi, 'list' | 'getNew' | 'getDetails' | 'updateStatus'>;
  /** Socket-burst coalesce window (06-realtime §4 "short window"). */
  coalesceMs?: number;
  /** Cap for the session-local recently-completed list (Done column). */
  recentlyCompletedLimit?: number;
}

const DEFAULT_COALESCE_MS = 250;
const DEFAULT_RECENT_LIMIT = 20;

export class OrderStore {
  // ── active board ─────────────────────────────────────────────────────────
  active: Order[] = [];
  activeStatus: LoadStatus = 'idle';
  activeError: ApiError | null = null;
  /** Epoch ms of the last successful active fetch (badge/tooltip display). */
  lastActiveSyncMs: number | null = null;

  /**
   * Session-local memory of orders this tab marked delivered — feeds the Done
   * column because GET /orders/new excludes delivered/cancelled. Cleared on
   * scope change/reload; the authoritative record stays in history.
   */
  recentlyCompleted: Order[] = [];

  /** Adopting day session: /orders/new filter + status-update adoption id. */
  daySessionId: number | null = null;

  /**
   * Has the board's shift filter been ESTABLISHED yet?
   *
   * `daySessionId === null` cannot answer this on its own: it is the same value
   * for "this till has no shift open" and for "the shift has not loaded yet",
   * and those need opposite behaviour. Unscoped, `GET /orders/new` returns every
   * active order the tenant has ever had — so treating the second case as the
   * first put a year of dead orders on the board for the second or two before
   * the session landed, oldest-first, on top of the order just taken. Scoping
   * the fetch fixed the steady state (see pos-routes.tsx); this closes the
   * window before it.
   */
  daySessionResolved = false;

  /** The queue asked for the board while the shift filter was still unknown. */
  private activeRequested = false;

  // ── history ──────────────────────────────────────────────────────────────
  history: Paginated<Order> | null = null;
  historyStatus: LoadStatus = 'idle';
  historyError: ApiError | null = null;
  historyParams: OrderListParams = {};
  historyStale = false;

  // ── detail ───────────────────────────────────────────────────────────────
  details = new Map<number, OrderDetailEntry>();
  openDetailId: number | null = null;

  /** Order ids with an in-flight status mutation (per-card busy state). */
  transitioningIds = new Set<number>();

  private readonly api: NonNullable<OrderStoreOptions['api']>;
  private readonly coalesceMs: number;
  private readonly recentLimit: number;
  private readonly seq = new RequestSequencer();
  private readonly activeListeners = new Set<(orders: Order[]) => void>();
  private coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  private activeStale = false;
  private staleDetailIds = new Set<number>();
  private reconcileInFlight = false;
  /** Bumped on reset so responses started under an old scope cannot land. */
  private epoch = 0;

  constructor(options: OrderStoreOptions = {}) {
    this.api = options.api ?? ordersApi;
    this.coalesceMs = options.coalesceMs ?? DEFAULT_COALESCE_MS;
    this.recentLimit = options.recentlyCompletedLimit ?? DEFAULT_RECENT_LIMIT;
    makeAutoObservable<
      OrderStore,
      | 'api'
      | 'coalesceMs'
      | 'recentLimit'
      | 'seq'
      | 'activeListeners'
      | 'coalesceTimer'
      | 'activeStale'
      | 'staleDetailIds'
      | 'reconcileInFlight'
      | 'epoch'
    >(
      this,
      {
        api: false,
        coalesceMs: false,
        recentLimit: false,
        seq: false,
        activeListeners: false,
        coalesceTimer: false,
        activeStale: false,
        staleDetailIds: false,
        reconcileInFlight: false,
        epoch: false,
      },
      { autoBind: true },
    );
  }

  // ── computeds ────────────────────────────────────────────────────────────

  /** Retained-data flag: board renders while a refresh runs behind it. */
  get hasActiveData(): boolean {
    return this.activeStatus === 'success' || this.activeStatus === 'refreshing'
      ? true
      : this.active.length > 0;
  }

  get openDetail(): OrderDetailEntry | null {
    return this.openDetailId === null ? null : (this.details.get(this.openDetailId) ?? null);
  }

  // ── wiring seams ─────────────────────────────────────────────────────────

  /** Lead/V1-06 wiring: current day session id (SessionStore). */
  setDaySessionId(id: number | null, resolved = true): void {
    if (this.daySessionId === id && this.daySessionResolved === resolved) return;
    this.daySessionId = id;
    this.daySessionResolved = resolved;
    // Still waiting to learn which shift is running — fetching now would ask
    // for every order in the tenant. The load runs the moment it resolves.
    if (!resolved) return;
    // The board filter is known, or has changed: reconcile when the queue has
    // asked for data (including a request deferred above).
    if (this.activeStatus !== 'idle' || this.activeRequested) void this.reconcileActive();
  }

  /**
   * Fires after every successful active-board fetch with the flat authoritative
   * list (RealtimeStore chime scan). Returns a disposer.
   */
  onActiveReconciled(listener: (orders: Order[]) => void): () => void {
    this.activeListeners.add(listener);
    return () => this.activeListeners.delete(listener);
  }

  // ── coalesced realtime entrypoint ────────────────────────────────────────

  /**
   * Socket-event hint: marks state stale and schedules ONE reconcile per
   * coalesce window. Detail hints re-fetch the affected open drawer; history
   * is invalidated lazily (refetched by its own surface/mutations).
   */
  notifyExternalChange(hint: ExternalChangeHint = { kind: 'new', orderId: null }): void {
    this.activeStale = true;
    this.historyStale = true;
    if (hint.orderId !== null && this.details.has(hint.orderId)) {
      this.staleDetailIds.add(hint.orderId);
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.coalesceTimer !== null || this.reconcileInFlight) return;
    this.coalesceTimer = setTimeout(() => {
      this.coalesceTimer = null;
      void this.flushStale();
    }, this.coalesceMs);
  }

  private async flushStale(): Promise<void> {
    if (!this.activeStale && this.staleDetailIds.size === 0) return;
    this.activeStale = false;
    const detailIds = [...this.staleDetailIds];
    this.staleDetailIds.clear();

    const refreshes: Promise<unknown>[] = [this.reconcileActive()];
    for (const id of detailIds) {
      // Only the open drawer re-fetches now; closed cached entries drop so a
      // future open fetches fresh instead of showing stale data.
      if (id === this.openDetailId) refreshes.push(this.fetchDetail(id));
      else this.details.delete(id);
    }
    await Promise.allSettled(refreshes);
    // Hints that arrived while we were reconciling → one more pass.
    if (this.activeStale || this.staleDetailIds.size > 0) this.scheduleFlush();
  }

  // ── active board ─────────────────────────────────────────────────────────

  /** Initial/queue-mount load; same fetch path as reconcile. */
  async loadActive(): Promise<void> {
    this.activeRequested = true;
    await this.reconcileActive();
  }

  /**
   * Fetches /orders/new and REPLACES the board (authoritative response —
   * upsert-by-id with no duplicate cards). Returns false on failure so the
   * polling fallback can back off. Stale responses (superseded, aborted, or
   * from a previous scope epoch) never land.
   */
  async reconcileActive(): Promise<boolean> {
    /*
      Held until the shift filter is known — see `daySessionResolved`.

      The gate lives HERE rather than only in loadActive() because the polling
      fallback and the realtime hints call straight into this method, and a poll
      that fired before the session landed fetched the whole tenant regardless of
      how carefully the mount path waited.

      Reported as loading, not left idle: the board is coming, and an idle board
      renders as "no orders" — a different and wrong thing to tell a counter with
      a queue in front of it. `setDaySessionId` runs the held load the moment the
      shift resolves.

      Returns TRUE, because nothing failed. The one caller that reads this value
      is the polling fallback, which counts a false as a transport error and
      backs the poll off exponentially — so a till sitting on the register with
      the queue unopened spent its wait inflating that backoff, and the first
      poll after the board finally opened was measured 97 s later against a 30 s
      cadence. "Not yet" is not "broken".
    */
    if (!this.daySessionResolved) {
      this.activeRequested = true;
      runInAction(() => {
        if (this.activeStatus === 'idle') this.activeStatus = 'loading';
      });
      return true;
    }

    const startEpoch = this.epoch;
    const ticket = this.seq.begin('orders:active');
    this.reconcileInFlight = true;
    this.activeStatus = this.hasActiveData ? 'refreshing' : 'loading';
    try {
      const board = await this.api.getNew(this.daySessionId, { signal: ticket.signal });
      if (!ticket.isCurrent() || this.epoch !== startEpoch) return false;
      const flat = this.inShift([...board.pending, ...board.inprogress]);
      runInAction(() => {
        this.active = flat;
        this.activeStatus = 'success';
        this.activeError = null;
        this.lastActiveSyncMs = Date.now();
      });
      this.emitActiveReconciled(flat);
      return true;
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent() || this.epoch !== startEpoch) return false;
      runInAction(() => {
        this.activeError = normalizeApiError(e);
        this.activeStatus = 'error';
      });
      return false;
    } finally {
      runInAction(() => {
        this.reconcileInFlight = false;
      });
      // A hint may have arrived while in flight — schedule the follow-up.
      if (this.activeStale || this.staleDetailIds.size > 0) this.scheduleFlush();
    }
  }

  /**
   * The board is a SHIFT board, in both directions.
   *
   * With a shift named, the server already answers correctly: that session's
   * orders plus the unassigned pending web ones. With no shift open it has no
   * filter to apply and returns every active order the tenant has ever had —
   * which is how the counter saw 143 orders, refreshed, and saw 229 including
   * ones a year old. The number appeared to change at random because it was
   * answering two different questions depending on whether a day happened to be
   * open at that moment.
   *
   * So when there is no shift, the same rule is applied here — and it is copied
   * from the server verbatim: an order no shift owns counts only while it is
   * still PENDING (`day_session_id IS NULL AND status = 'pending'`,
   * OrderController::getNewOrders). Both halves matter. Dropping the status half
   * kept 37 unclaimed orders that were already preparing or ready, so a closed
   * day showed 180 where the same board with a shift open showed 143 — the
   * numbers still moved, which is the whole complaint.
   *
   * Orders belonging to OTHER, older sessions are not lost — they are in
   * history, which is where a closed shift's work belongs.
   */
  private inShift(orders: Order[]): Order[] {
    if (this.daySessionId != null) return orders;
    return orders.filter((order) => order.daySessionId == null && order.status === 'pending');
  }

  private emitActiveReconciled(orders: Order[]): void {
    for (const listener of this.activeListeners) {
      try {
        listener(orders);
      } catch {
        // one listener failing must not break reconciliation
      }
    }
  }

  // ── history ──────────────────────────────────────────────────────────────

  async loadHistory(params: OrderListParams = this.historyParams): Promise<void> {
    const startEpoch = this.epoch;
    const ticket = this.seq.begin('orders:history');
    this.historyParams = params;
    this.historyStatus = this.history ? 'refreshing' : 'loading';
    try {
      const page = await this.api.list(params, { signal: ticket.signal });
      if (!ticket.isCurrent() || this.epoch !== startEpoch) return;
      runInAction(() => {
        this.history = page;
        this.historyStatus = 'success';
        this.historyError = null;
        this.historyStale = false;
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent() || this.epoch !== startEpoch) return;
      runInAction(() => {
        this.historyError = normalizeApiError(e);
        this.historyStatus = 'error';
      });
    }
  }

  /** Explicit invalidation; refetches immediately when a page is loaded. */
  invalidateHistory(): void {
    this.historyStale = true;
    if (this.history) void this.loadHistory();
  }

  // ── detail ───────────────────────────────────────────────────────────────

  /** Opens the drawer for an order and (re-)fetches its authoritative detail. */
  openOrder(orderId: number): void {
    this.openDetailId = orderId;
    void this.fetchDetail(orderId);
  }

  closeOrder(): void {
    this.openDetailId = null;
  }

  async fetchDetail(orderId: number): Promise<void> {
    const startEpoch = this.epoch;
    const ticket = this.seq.begin(`orders:detail:${orderId}`);
    const existing = this.details.get(orderId);
    this.details.set(orderId, {
      order: existing?.order ?? null,
      status: existing?.order ? 'refreshing' : 'loading',
      error: null,
    });
    try {
      const order = await this.api.getDetails(orderId, { signal: ticket.signal });
      if (!ticket.isCurrent() || this.epoch !== startEpoch) return;
      runInAction(() => {
        this.details.set(orderId, { order, status: 'success', error: null });
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent() || this.epoch !== startEpoch) return;
      const error = normalizeApiError(e);
      runInAction(() => {
        const entry = this.details.get(orderId);
        this.details.set(orderId, {
          order: entry?.order ?? null,
          status: 'error',
          error,
        });
      });
    }
  }

  // ── status mutation ──────────────────────────────────────────────────────

  /**
   * PUT /orders/{id}/status then RE-FETCH (the response has no order body).
   * One in-flight mutation per order; the current API enforces NO transition
   * matrix, so callers must offer only the vetted stage-table transitions.
   * `daySessionId` rides along for website-order adoption (applied upstream
   * only when the order has no session).
   */
  async transition(
    orderId: number,
    toStatus: string,
    options: { notes?: string } = {},
  ): Promise<TransitionResult> {
    if (this.transitioningIds.has(orderId)) {
      return {
        ok: false,
        error: { status: 0, message: 'This order already has an update in progress' },
      };
    }
    const startEpoch = this.epoch;
    this.transitioningIds.add(orderId);
    try {
      await this.api.updateStatus(orderId, toStatus, {
        ...(options.notes ? { notes: options.notes } : {}),
        ...(this.daySessionId != null ? { daySessionId: this.daySessionId } : {}),
      });
      if (this.epoch !== startEpoch)
        return { ok: false, error: { status: 0, message: 'Scope changed' } };

      if (toStatus === 'delivered') {
        const known =
          this.active.find((o) => o.id === orderId) ?? this.details.get(orderId)?.order ?? null;
        if (known) this.captureCompleted(known);
      }

      const refreshes: Promise<unknown>[] = [this.reconcileActive()];
      if (this.details.has(orderId)) refreshes.push(this.fetchDetail(orderId));
      if (this.history) refreshes.push(this.loadHistory());
      await Promise.allSettled(refreshes);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: normalizeApiError(e) };
    } finally {
      runInAction(() => {
        this.transitioningIds.delete(orderId);
      });
    }
  }

  /**
   * Reconcile everything after an order was EDITED elsewhere in the UI (V2.3).
   *
   * The edit endpoint DOES return the authoritative order, unlike the status
   * PUT — so the detail cache is seeded from it immediately and the operator
   * sees the corrected order without a flash of the old one. The board and any
   * loaded history are still re-fetched, because the edit changed totals and
   * item counts that other surfaces render, and because the same edit
   * broadcasts to every other terminal anyway.
   *
   * The order's STAGE is untouched here, deliberately: an edit does not advance
   * an order, so a card must not move columns because someone fixed an address.
   */
  async applyEditedOrder(order: Order): Promise<void> {
    runInAction(() => {
      this.details.set(order.id, { order, status: 'success', error: null });
      // A delivered order that was corrected stays in the session-local Done
      // column, but with its new figures — otherwise the card would keep
      // showing the total the guest was originally charged.
      this.recentlyCompleted = this.recentlyCompleted.map((entry) =>
        entry.id === order.id ? { ...order, status: entry.status } : entry,
      );
    });

    const refreshes: Promise<unknown>[] = [this.reconcileActive()];
    if (this.history) refreshes.push(this.loadHistory());
    await Promise.allSettled(refreshes);
  }

  /**
   * Remembers a just-delivered order for the session-local Done column (the
   * active endpoint stops returning it). Status is set to the CONFIRMED write
   * ('delivered'); everything else is the last authoritative snapshot.
   */
  async captureCompletedOrder(orderId: number): Promise<void> {
    const known =
      this.active.find((o) => o.id === orderId) ?? this.details.get(orderId)?.order ?? null;
    if (known) {
      this.captureCompleted(known);
      return;
    }
    try {
      const order = await this.api.getDetails(orderId);
      if (order.status === 'delivered') this.captureCompleted(order);
    } catch {
      // Checkout is already authoritative; a failed detail read is non-fatal.
    }
  }

  private captureCompleted(order: Order): void {
    const completed: Order = { ...order, status: 'delivered' };
    runInAction(() => {
      this.recentlyCompleted = [
        completed,
        ...this.recentlyCompleted.filter((o) => o.id !== order.id),
      ].slice(0, this.recentLimit);
    });
  }

  // ── reset seam (lead wires auth scope listener → reset) ──────────────────

  reset(): void {
    this.epoch += 1;
    this.seq.abortAll();
    if (this.coalesceTimer !== null) {
      clearTimeout(this.coalesceTimer);
      this.coalesceTimer = null;
    }
    this.activeStale = false;
    this.staleDetailIds.clear();
    this.reconcileInFlight = false;

    this.active = [];
    this.activeStatus = 'idle';
    this.activeError = null;
    this.lastActiveSyncMs = null;
    this.recentlyCompleted = [];
    // The shift filter belonged to the OLD scope, so it is unknown again — not
    // "known to be absent". Leaving `daySessionResolved` true here let the
    // polling fallback fetch the whole tenant unscoped in the window between a
    // terminal switch and the queue re-resolving the new terminal's shift.
    // QueueRoute re-asserts the filter (it depends on this flag), so clearing
    // it cannot strand the board.
    this.daySessionId = null;
    this.daySessionResolved = false;
    this.activeRequested = false;

    this.history = null;
    this.historyStatus = 'idle';
    this.historyError = null;
    this.historyParams = {};
    this.historyStale = false;

    this.details.clear();
    this.openDetailId = null;
    this.transitioningIds.clear();
  }
}
